<template>
  <div>
    <v-row class="mb-2" align="center" dense>
      <v-col cols="6" sm="3">
        <dropdown
          v-model="filterPeriod"
          :items="periodOptions"
          itemText="label"
          itemValue="value"
          :label="$t('finance_period_label')"
          hideDetails
        />
      </v-col>
      <v-col cols="6" sm="3">
        <dropdown
          v-model="filterStatus"
          :items="statusOptions"
          itemText="label"
          itemValue="value"
          :label="$t('finance_status_label')"
          clearable
          hideDetails
        />
      </v-col>
    </v-row>

    <v-card outlined>
      <v-data-table
        :headers="headers"
        :items="filteredPayouts"
        :loading="loading"
        :loading-text="$t('finance_loading')"
        :no-data-text="$t('finance_no_payouts')"
        mobile-breakpoint="0"
        @click:row="openPayout"
      >
        <template #item.date="{ item }">{{ formatDate(item.date) }}</template>
        <template #item.amount="{ item }">{{ formatCurrency(item.amount, restaurantCurrency) }}</template>
        <template #item.status="{ item }">
          <v-chip small :color="statusMeta(item.status).color" text-color="white">
            {{ $t(statusMeta(item.status).labelKey) }}
          </v-chip>
        </template>
      </v-data-table>
      <p v-if="!loading && filteredPayouts.length > 0" class="hint-copy px-3 pb-2">
        {{ $t("finance_click_payout_bank_details_hint") }}
      </p>
    </v-card>

    <restaurant-payout-detail-dialog
      v-model="detailOpen"
      :payout="selectedPayout"
      @pdf="mockAction($t('finance_pdf_statement'))"
    />

    <v-snackbar v-model="snackbarOpen" :timeout="2500">{{ snackbarMessage }}</v-snackbar>
  </div>
</template>

<script>
import gpsApi from "@/plugins/gpsApi";
import hasSnackbar from "@/mixins/hasSnackbar";
import RestaurantPayoutDetailDialog from "./dialogs/RestaurantPayoutDetailDialog.vue";
import { RESTAURANT_PAYOUT_STATUS_META, formatCurrency, formatDate } from "./constants";

// Isplatu evidentira platforma/banka - restoranska strana je samo za čitanje.
const mapRestaurantPayoutDto = (dto) => ({
  id: dto.id,
  date: dto.date,
  amount: dto.amount,
  period: dto.period,
  status: dto.status,
  bankAccount: dto.bank_account,
  reference: dto.reference,
});

const isWithinPeriod = (date, value) => {
  if (value === "all") return true;
  const target = new Date(date);
  const now = new Date();
  if (value === "year") return target.getFullYear() === now.getFullYear();
  const days = value === "30d" ? 30 : 90;
  return now.getTime() - target.getTime() <= days * 24 * 60 * 60 * 1000;
};

export default {
  name: "FinanceTabRestaurantPayouts",

  components: { RestaurantPayoutDetailDialog },

  mixins: [hasSnackbar],

  data() {
    return {
      payouts: [],
      loading: false,
      filterPeriod: "all",
      filterStatus: null,
      detailOpen: false,
      selectedPayout: null,
      snackbarOpen: false,
      snackbarMessage: "",
    };
  },

  computed: {
    restaurantId() {
      return this.$store.state.restaurant.id;
    },
    periodOptions() {
      return [
        { label: this.$t("finance_all"), value: "all" },
        { label: this.$t("finance_last_30_days"), value: "30d" },
        { label: this.$t("finance_last_90_days"), value: "90d" },
        { label: this.$t("finance_this_year"), value: "year" },
      ];
    },
    headers() {
      return [
        { text: this.$t("finance_date"), value: "date" },
        { text: this.$t("finance_amount"), value: "amount", align: "end" },
        { text: this.$t("finance_period_label"), value: "period" },
        { text: this.$t("finance_status_label"), value: "status" },
      ];
    },
    statusOptions() {
      return Object.keys(RESTAURANT_PAYOUT_STATUS_META).map((value) => ({
        value,
        label: this.$t(RESTAURANT_PAYOUT_STATUS_META[value].labelKey),
      }));
    },
    filteredPayouts() {
      return this.payouts.filter(
        (payout) =>
          isWithinPeriod(payout.date, this.filterPeriod) &&
          (!this.filterStatus || payout.status === this.filterStatus)
      );
    },
  },

  created() {
    this.refresh();
  },

  methods: {
    formatCurrency,
    formatDate,

    statusMeta(status) {
      return RESTAURANT_PAYOUT_STATUS_META[status] || { labelKey: "", color: "grey" };
    },

    async refresh() {
      if (!this.restaurantId) return;
      this.loading = true;
      try {
        const { data } = await gpsApi.get(`/restaurants/${this.restaurantId}/bank-payouts`);
        const list = (data && (data.data || data)) || [];
        this.payouts = list.map(mapRestaurantPayoutDto);
      } catch (error) {
        this.showErrorSnackbar(this.$t("finance_restaurant_payouts_load_error"));
      } finally {
        this.loading = false;
      }
    },

    openPayout(payout) {
      this.selectedPayout = payout;
      this.detailOpen = true;
    },

    mockAction(action) {
      this.snackbarMessage = this.$t("finance_action_not_connected", { action });
      this.snackbarOpen = true;
    },
  },
};
</script>

<style scoped>
.hint-copy {
  margin: 0;
  font-size: 0.78rem;
  color: rgba(0, 0, 0, 0.45);
}
</style>
